import { useEffect, useRef, useState } from 'react';
import { D, formatMoney, PAYMENT_METHODS, parseMoney, type PaymentMethod } from '@makila/fiscal';
import { type Giftcard, settle, type Tender, voucherReference, voucherUsable } from '@makila/pos-core';
import { Banknote, CreditCard, Loader2, Smartphone, X } from 'lucide-react';

const METHODS: { m: PaymentMethod; icon: typeof Banknote }[] = [
  { m: 'NU', icon: Banknote }, { m: 'MP', icon: Smartphone }, { m: 'EM', icon: Smartphone }, { m: 'MK', icon: Smartphone }, { m: 'CC', icon: CreditCard },
];
const NOTES = ['100', '200', '500', '1000'];
const NEEDS_REF: PaymentMethod[] = ['MP', 'EM', 'MK'];

/**
 * Pagamento: um ou vários meios (misto). O troco só sai do numerário. No MVP
 * o M-Pesa/e-Mola/mKesh registam-se à mão: a referência da SMS é obrigatória
 * (decisão de 20/10/2026; integração C2B depois do piloto).
 */
export function PayDialog(p: { total: string; credit?: { amount: string; reference: string }; hint?: string; onClose: () => void; onConfirm: (t: Tender[]) => Promise<void>;
  /** Vales-oferta (decisão F2): procurar o vale pelo código (catálogo da caixa). */
  voucherLookup?: (code: string) => Promise<Giftcard | undefined>;
}) {
  const [voucher, setVoucher] = useState('');
  // Crédito de uma troca: entra como numerário (o mesmo que a NC devolveu à gaveta) e não se retira; o que sobrar é troco.
  const [tenders, setTenders] = useState<Tender[]>(() => (p.credit ? [{ method: 'NU', amount: p.credit.amount, reference: p.credit.reference }] : []));
  const [method, setMethod] = useState<PaymentMethod>('NU');
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dlg = useRef<HTMLDialogElement>(null);
  const amountInput = useRef<HTMLInputElement>(null);

  useEffect(() => { dlg.current?.showModal(); amountInput.current?.focus(); }, []);

  const paid = tenders.reduce((s, t) => s.plus(t.amount), new D(0));
  const remaining = D.max(new D(p.total).minus(paid), 0);
  let result: ReturnType<typeof settle> | null = null;
  try { result = tenders.length ? settle(p.total, tenders) : null; } catch { result = null; }

  const addTender = (value?: string) => {
    setError(null);
    const a = parseMoney(value ?? (amount || remaining.toFixed(2)));
    if (!a || !new D(a).greaterThan(0)) { setError('Valor inválido'); return; }
    if (NEEDS_REF.includes(method) && !ref.trim()) { setError(`${PAYMENT_METHODS[method]}: escreva a referência da SMS de confirmação`); return; }
    if (method !== 'NU' && new D(a).greaterThan(remaining)) { setError('Só o numerário pode passar do total (troco)'); return; }
    setTenders((t) => [...t, { method, amount: a, reference: ref.trim() || null }]);
    setAmount('');
    setRef('');
    setMethod('NU');
    amountInput.current?.focus();
  };

  /** Vale como pagamento: o que der (até ao saldo e ao que falta), no meio "Outro" com a referência do código. */
  const useVoucher = async () => {
    setError(null);
    const code = voucher.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/^(.{4})(.{4})$/, '$1-$2');
    if (tenders.some((t) => t.reference === voucherReference(code))) { setError('Esse vale já está neste pagamento'); return; }
    const u = voucherUsable(await p.voucherLookup!(code), remaining.toFixed(2));
    if (u.reason) { setError(u.reason); return; }
    setTenders((t) => [...t, { method: 'OU', amount: u.amount, reference: voucherReference(code) }]);
    setVoucher('');
  };

  const confirm = async () => {
    if (!result) return;
    setBusy(true);
    setError(null);
    try { await p.onConfirm(tenders); } catch (e) { setError((e as Error).message); setBusy(false); }
  };

  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); if (!busy) p.onClose(); }} className="m-auto w-[min(640px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">Pagamento</h2>
        <button aria-label="Fechar" disabled={busy} onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="p-5">
        <div className="flex items-baseline justify-between">
          <span className="text-muted">Total a pagar</span>
          <span className="num text-4xl font-bold">{formatMoney(p.total)} MT</span>
        </div>
        {p.hint && <p className="mt-2 rounded-lg bg-surface-2 p-2 text-sm">{p.hint}</p>}
        {tenders.length > 0 && (
          <ul className="mt-3 space-y-1">
            {tenders.map((t, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2">
                <span>{p.credit && i === 0 ? 'Crédito da troca' : PAYMENT_METHODS[t.method]}{t.reference ? <small className="ml-2 text-muted">{t.reference}</small> : null}</span>
                <span className="num flex items-center gap-2 font-semibold">{formatMoney(t.amount)}
                  {!(p.credit && i === 0) && <button aria-label="Retirar pagamento" onClick={() => setTenders((x) => x.filter((_, j) => j !== i))} className="grid h-12 w-12 place-items-center rounded-lg"><X size={16} /></button>}
                </span>
              </li>
            ))}
          </ul>
        )}

        {!result && (
          <>
            <div className="mt-4 grid grid-cols-5 gap-2">
              {METHODS.map(({ m, icon: Icon }) => (
                <button key={m} onClick={() => setMethod(m)} className={`flex h-16 flex-col items-center justify-center rounded-xl border text-sm font-medium ${method === m ? 'border-brand-700 bg-brand-700 text-white' : 'border-base'}`}>
                  <Icon size={20} />{PAYMENT_METHODS[m].replace('Cartão (POS)', 'Cartão')}
                </button>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                ref={amountInput}
                inputMode="decimal"
                placeholder={formatMoney(remaining)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTender(); } }}
                aria-label="Valor entregue"
                className="num h-14 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-2xl"
              />
              <button onClick={() => addTender()} className="h-14 rounded-xl bg-charcoal px-5 font-semibold text-white">Adicionar</button>
            </div>
            {p.voucherLookup && remaining.greaterThan(0) && (
              <div className="mt-2 flex gap-2">
                <input aria-label="Código do vale" placeholder="Vale-oferta: código (XXXX-XXXX)" value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void useVoucher(); } }}
                  className="h-12 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 font-mono uppercase tracking-wider" />
                <button disabled={voucher.replace(/[^A-Za-z0-9]/g, '').length !== 8} onClick={() => void useVoucher()} className="h-12 rounded-xl border border-base px-4 font-semibold disabled:opacity-40">Usar vale</button>
              </div>
            )}
            {NEEDS_REF.includes(method) && (
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Referência da SMS (obrigatória)" aria-label="Referência da transacção"
                className="mt-2 h-12 w-full rounded-xl border border-base bg-surface-2 px-4 font-mono" />
            )}
            {method === 'NU' && (
              <div className="mt-2 grid grid-cols-5 gap-2">
                <button onClick={() => addTender(remaining.toFixed(2))} className="h-12 rounded-xl border border-base font-medium">Exacto</button>
                {NOTES.map((n) => <button key={n} onClick={() => addTender(n)} className="num h-12 rounded-xl border border-base font-medium">{n}</button>)}
              </div>
            )}
          </>
        )}

        {result && (
          <div className="mt-4 rounded-xl bg-green-50 p-4 text-green-900 dark:bg-green-950 dark:text-green-100">
            <div className="flex justify-between"><span>Entregue</span><span className="num font-semibold">{formatMoney(result.tendered)}</span></div>
            <div className="num mt-1 flex items-baseline justify-between text-3xl font-bold"><span>Troco</span><span>{formatMoney(result.change)} MT</span></div>
          </div>
        )}
        {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
        <button
          disabled={!result || busy}
          onClick={confirm}
          className="mt-5 flex h-16 w-full items-center justify-center gap-2 rounded-xl bg-brand-700 text-2xl font-bold text-white disabled:opacity-40"
        >{busy && <Loader2 className="animate-spin" />} Emitir e imprimir</button>
      </div>
    </dialog>
  );
}
